'use client';

import { Grid2X2, List, Loader2, UploadCloud } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useFileUpload } from '../../hooks/useFileUpload';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { AttachmentList } from '../attachment-list';
import { FileDropzone, useFileDropzone } from '../file-dropzone';
import { FileUploadQueue } from '../file-upload-queue';

export interface ContactFilesTabProps {
  contactId: number;
  isReadOnly?: boolean;
}

export function ContactFilesTab({ contactId, isReadOnly = false }: ContactFilesTabProps) {
  const utils = trpc.useUtils();

  const { data: files, isLoading } = trpc.contacts.getFiles.useQuery(
    { contactId },
    { enabled: !!contactId }
  );

  const { mutateAsync: createAssociation } = trpc.files.createAssociation.useMutation();
  const { mutateAsync: dissociate } = trpc.files.dissociate.useMutation();
  const { mutateAsync: updateFile } = trpc.files.update.useMutation();

  const { cancelUpload, isUploading, uploadAll, uploadStatuses } = useFileUpload();

  const invalidate = () => utils.contacts.getFiles.invalidate({ contactId });

  const [layout, setLayout] = useState<'list' | 'grid'>(() =>
    window.localStorage.getItem('contact-files-layout') === 'grid' ? 'grid' : 'list'
  );
  const changeLayout = (nextLayout: 'list' | 'grid') => {
    setLayout(nextLayout);
    window.localStorage.setItem('contact-files-layout', nextLayout);
  };

  const handleUpload = async (droppedFiles: File[]) => {
    if (!contactId || isReadOnly) return;

    const promise = (async () => {
      const fileIds = await uploadAll(droppedFiles);
      if (fileIds.length === 0) return;

      // Associate with contact directly as "General"
      await Promise.all(
        fileIds.map((fileId) =>
          createAssociation({
            fileId,
            contactId,
            category: 'General',
          })
        )
      );

      await invalidate();
    })();

    toast.promise(promise, {
      loading: 'Uploading files...',
      success: 'Files uploaded successfully',
      error: 'Failed to upload files',
    });
  };

  const handleRemove = async (assocId: number) => {
    try {
      await dissociate({ id: assocId });
      await invalidate();
      toast.success('File removed');
    } catch (_error) {
      toast.error('Failed to remove file');
    }
  };

  const handleRename = async (fileId: number, newName: string) => {
    try {
      await updateFile({ id: fileId, label: newName });
      await invalidate();
      toast.success('File renamed');
    } catch (_error) {
      toast.error('Failed to rename file');
    }
  };

  const sections = useMemo(() => {
    if (!files) return [];

    const result: { title: string; items: typeof files }[] = [];

    // 1. General Attachments
    const general = files.filter(
      (f) => !f.threadId && !f.commentId && (!f.category || f.category === 'General')
    );
    if (general.length > 0) {
      result.push({ title: 'General Attachments', items: general });
    }

    // 2. Custom Attribute Fields
    const fieldFiles = files.filter(
      (f) => !f.threadId && !f.commentId && f.category && f.category !== 'General'
    );
    const categories = Array.from(new Set(fieldFiles.map((f) => f.category!)));

    categories.forEach((cat) => {
      const items = fieldFiles.filter((f) => f.category === cat);
      result.push({ title: cat, items });
    });

    // 3. Discussions & Threads
    const threadFiles = files.filter((f) => f.threadId || f.commentId);
    if (threadFiles.length > 0) {
      result.push({ title: 'Discussions & Threads', items: threadFiles });
    }

    return result;
  }, [files]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 flex flex-col flex-1 min-h-0 overflow-y-auto pr-1">
      {!isReadOnly && (
        <>
          <FileDropzone
            onFilesDrop={handleUpload}
            noClick
            className="rounded-xl border border-dashed border-border bg-muted/20 p-3"
          >
            <ContactFileUploadPrompt isUploading={isUploading} />
          </FileDropzone>
          <FileUploadQueue statuses={uploadStatuses} onCancel={cancelUpload} />
        </>
      )}

      {sections.length > 0 && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground">Files</span>
          <div className="inline-flex rounded-md border border-border bg-background p-0.5">
            <button
              type="button"
              aria-label="List view"
              aria-pressed={layout === 'list'}
              onClick={() => changeLayout('list')}
              className={`rounded p-1.5 ${layout === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}
            >
              <List className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Grid view"
              aria-pressed={layout === 'grid'}
              onClick={() => changeLayout('grid')}
              className={`rounded p-1.5 ${layout === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}
            >
              <Grid2X2 className="size-4" />
            </button>
          </div>
        </div>
      )}

      {sections.map((section) => (
        <div key={section.title} className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border/60" />
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2 px-1">
              {section.title}
              <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-full text-[9px]">
                {section.items.length}
              </span>
            </h3>
            <div className="h-px flex-1 bg-border/60" />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <AttachmentList
              attachments={section.items.map((file) => ({
                type: 'remote',
                fileId: file.id,
                fileName: file.fileName,
                fileType: file.fileType,
                associationId: file.associationId,
                viewUrl: file.viewUrl,
              }))}
              onRemove={
                isReadOnly ? undefined : (index) => handleRemove(section.items[index].associationId)
              }
              onRename={
                isReadOnly
                  ? undefined
                  : (index, newName) => handleRename(section.items[index].id, newName)
              }
              layout={layout}
              className="border-none bg-transparent p-0"
            />
          </div>
        </div>
      ))}

      {(!files || files.length === 0) && !isUploading && (
        <div className="text-center py-12 flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-slate-50 dark:bg-slate-900/60">
          <div className="size-10 rounded-full bg-muted/30 flex items-center justify-center text-muted-foreground/40">
            <UploadCloud className="size-5" />
          </div>
          <p className="text-xs text-muted-foreground italic">
            No files associated with this contact yet.
          </p>
        </div>
      )}
    </div>
  );
}

function ContactFileUploadPrompt({ isUploading }: { isUploading: boolean }) {
  const { openFilePicker } = useFileDropzone();

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        {isUploading ? (
          <Loader2 className="size-5 shrink-0 animate-spin text-primary" />
        ) : (
          <UploadCloud className="size-5 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium">Upload files for this contact</p>
          <p className="text-xs text-muted-foreground">
            Drag files here, or choose files to upload.
          </p>
        </div>
      </div>
      <Button type="button" size="sm" className="shrink-0" onClick={openFilePicker}>
        Upload files
      </Button>
    </div>
  );
}
